import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Copy, Landmark, Loader2 } from "lucide-react";
import { useCheckoutRouting } from "@/hooks/useCheckoutRouting";
import { logError } from "@/services/errorLogger";

/**
 * Route Pro : l'acheteur règle le vendeur par virement direct.
 * TAMA n'encaisse rien ici et ne fait qu'émettre une référence de suivi.
 */

export type TransferZone = "polynesie" | "sepa";

interface TransferIntentDetails {
  id: string;
  reference: string;
  status: string;
  zone: TransferZone;
  amount_xpf: number;
  account_holder: string;
  bank_name: string;
  iban: string | null;
  bic: string | null;
  local_account_hint: string | null;
}

interface TransferPurchasePanelProps {
  productId: string;
  priceXpf: number;
  sellerName: string;
}

const STATUS_LABELS: Record<string, string> = {
  opened: "En attente de votre virement",
  buyer_declared_paid: "À confirmer",
  seller_confirmed: "Reçu",
  disputed: "Litige signalé par le vendeur",
  cancelled: "Annulée",
};

const formatXpf = (amount: number) => `${Math.round(amount).toLocaleString("fr-FR")} F CFP`;

export const TransferPurchasePanel = ({ productId, priceXpf, sellerName }: TransferPurchasePanelProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { routing } = useCheckoutRouting();
  const [zone, setZone] = useState<TransferZone>("polynesie");
  const [intent, setIntent] = useState<TransferIntentDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [declaring, setDeclaring] = useState(false);

  /** Recharge le statut d'une intention déjà ouverte pour ce produit. */
  const refreshExistingIntent = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("transfer_intents" as any)
      .select("id, status")
      .eq("buyer_id", user.id)
      .eq("product_id", productId)
      .in("status", ["opened", "buyer_declared_paid"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (data) {
      setIntent((prev) => (prev ? { ...prev, status: (data as any).status } : prev));
    }
  }, [productId, user]);

  useEffect(() => {
    refreshExistingIntent();
  }, [refreshExistingIntent]);

  const handleRequestDetails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("create_transfer_intent" as any, {
        p_product_id: productId,
        p_zone: zone,
      });
      if (error) throw error;
      setIntent(data as unknown as TransferIntentDetails);
    } catch (error: any) {
      logError("Création intention de virement", error);
      toast({
        variant: "destructive",
        title: "Virement indisponible",
        description: error?.message ?? "Réessayez dans un instant.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeclarePaid = async () => {
    if (!intent) return;
    setDeclaring(true);
    try {
      const { error } = await supabase.rpc("declare_transfer_paid" as any, { p_intent_id: intent.id });
      if (error) throw error;
      setIntent({ ...intent, status: "buyer_declared_paid" });
      toast({
        title: "Virement déclaré",
        description:
          "Commande visible dans Profil > Paiements et commandes > Mes achats et commandes, badge « À confirmer ».",
      });
    } catch (error: any) {
      logError("Déclaration de virement", error);
      toast({
        variant: "destructive",
        title: "Action impossible",
        description: error?.message ?? "Réessayez dans un instant.",
      });
    } finally {
      setDeclaring(false);
    }
  };

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: "Copié", description: label });
    } catch {
      toast({ variant: "destructive", title: "Copie impossible", description: "Sélectionnez le texte manuellement." });
    }
  };

  const amountEur = priceXpf / routing.xpfPerEur;

  return (
    <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/40">
      <div className="flex items-start gap-3">
        <Landmark className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1">
          <p className="font-semibold">Paiement par virement au vendeur</p>
          <p className="text-sm text-muted-foreground">
            Vous réglez directement {sellerName}. TAMA n'encaisse pas cette vente et n'intervient pas
            dans la transaction : vérifiez toujours les coordonnées avant d'envoyer les fonds.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between rounded-md bg-background p-3">
        <span className="text-sm text-muted-foreground">Montant à virer</span>
        <div className="text-right">
          <p className="font-semibold">{formatXpf(priceXpf)}</p>
          {/* L'équivalent euro reste visible quelle que soit la zone de virement. */}
          <p className="text-xs text-muted-foreground">
            ≈ {amountEur.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € (indicatif)
          </p>
        </div>
      </div>


      {!intent ? (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="transfer-zone">
              Depuis quelle zone virez-vous ? <span className="text-destructive">*</span>
            </Label>
            <Select value={zone} onValueChange={(value) => setZone(value as TransferZone)}>
              <SelectTrigger id="transfer-zone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="polynesie">Polynésie française (virement local)</SelectItem>
                <SelectItem value="sepa">Zone SEPA (IBAN / BIC)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleRequestDetails} disabled={loading || !user} className="w-full gap-2">
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Obtenir les coordonnées de virement
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Statut</span>
            <Badge variant="secondary">{STATUS_LABELS[intent.status] ?? intent.status}</Badge>
          </div>

          <dl className="space-y-2 rounded-md bg-background p-3 text-sm">
            <CopyRow label="Référence à indiquer" value={intent.reference} onCopy={copy} />
            <CopyRow label="Titulaire du compte" value={intent.account_holder} onCopy={copy} />
            <CopyRow label="Banque" value={intent.bank_name} onCopy={copy} />
            {intent.iban && <CopyRow label="IBAN" value={intent.iban} onCopy={copy} />}
            {intent.bic && <CopyRow label="BIC" value={intent.bic} onCopy={copy} />}
            {intent.local_account_hint && (
              <CopyRow label="Consignes locales" value={intent.local_account_hint} onCopy={copy} />
            )}
          </dl>

          <p className="text-xs text-muted-foreground">
            Indiquez impérativement la référence <strong>{intent.reference}</strong> dans le libellé du
            virement pour que le vendeur puisse l'identifier.
          </p>

          {intent.status === "opened" && (
            <Button onClick={handleDeclarePaid} disabled={declaring} variant="secondary" className="w-full gap-2">
              {declaring && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              J'ai effectué le virement
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const CopyRow = ({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (label: string, value: string) => void;
}) => (
  <div className="flex items-start justify-between gap-2">
    <dt className="text-muted-foreground shrink-0">{label}</dt>
    <dd className="flex items-center gap-1 text-right font-medium break-all">
      {value}
      <button
        type="button"
        onClick={() => onCopy(label, value)}
        aria-label={`Copier ${label}`}
        className="p-1 rounded hover:bg-muted"
      >
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </dd>
  </div>
);

export default TransferPurchasePanel;
